import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../../src/lib/supabase';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import BoletoEmissionModal from '../BoletoEmissionModal';
import BoletoEmitidoModal from '../BoletoEmitidoModal';
import CobrancaEnviadaModal from '../CobrancaEnviadaModal';
import type { Fatura } from '../../../types';
import { STATUS_DA_ACAO } from '../../../supabase/functions/_shared/etapaBoletoParcela.ts';

// Abre a fatura de uma parcela de acordo na MESMA tela que o quadro de Faturas usa
// para aquele passo (#6540): emitir, enviar ao associado ou a cobrança já enviada
// (ver boleto, reenviar o e-mail, prorrogar). Nada de envio novo: é o fluxo das
// faturas mensais, com as travas dele.
//
// Como no quadro de Faturas: pega a trava da fatura (ninguém mais mexe nela
// enquanto está aberta), mantém a trava viva e, depois de cada passo, relê a
// fatura — emitir leva direto para a tela de envio.

const STATUS_COM_TELA = new Set<string>(Object.values(STATUS_DA_ACAO));
const BATIMENTO_TRAVA_MS = 90_000;

export function useFaturaDaParcela(onAtualizar?: () => void) {
    const { user } = useAuth();
    const { addToast } = useToast();
    const [aberta, setAberta] = useState<Fatura | null>(null);
    const [abrindo, setAbrindo] = useState<number | null>(null);
    // Refs: o fechamento e a releitura rodam depois de awaits e não podem usar a
    // fatura/usuário de um render antigo.
    const abertaIdRef = useRef<number | null>(null);
    const userIdRef = useRef<string | undefined>(user?.id);
    userIdRef.current = user?.id;
    const onAtualizarRef = useRef(onAtualizar);
    onAtualizarRef.current = onAtualizar;

    const liberarTrava = useCallback(async (faturaId: number | null) => {
        const uid = userIdRef.current;
        if (!faturaId || !uid) return;
        await supabase.rpc('release_fatura_lock_v2', { p_fatura_id: faturaId, p_user_id: uid });
    }, []);

    // Trava viva enquanto a tela está aberta (expira em 10 min sem batimento).
    useEffect(() => {
        const id = aberta?.id;
        if (!id || !user?.id) return;
        const t = setInterval(() => {
            supabase.rpc('heartbeat_fatura_lock_v2', { p_fatura_id: Number(id), p_user_id: user.id });
        }, BATIMENTO_TRAVA_MS);
        return () => clearInterval(t);
    }, [aberta?.id, user?.id]);

    // Sair da tela com a fatura aberta não deixa a trava presa.
    useEffect(() => () => { liberarTrava(abertaIdRef.current); }, [liberarTrava]);

    const abrir = useCallback(async (faturaId: number) => {
        if (!userIdRef.current) {
            addToast('Usuário não autenticado.', 'error');
            return;
        }
        setAbrindo(faturaId);
        try {
            const { data: trava, error: erroTrava } = await supabase.rpc('acquire_fatura_lock_v2', {
                p_fatura_id: faturaId,
                p_user_id: userIdRef.current,
            });
            if (erroTrava) {
                addToast('Não consegui verificar se a fatura está livre. Tente de novo.', 'error');
                return;
            }
            const r = trava?.[0];
            if (r && !r.success) {
                addToast(`Esta fatura está aberta por ${r.locked_by_name || 'outra pessoa'}.`, 'warning');
                return;
            }
            const { data, error } = await supabase.from('faturas').select('*').eq('id', faturaId).maybeSingle();
            if (error || !data) {
                await liberarTrava(faturaId);
                addToast('Não consegui abrir a fatura desta parcela.', 'error');
                return;
            }
            if (!STATUS_COM_TELA.has(data.status_fatura)) {
                await liberarTrava(faturaId);
                addToast(`A fatura desta parcela está como "${data.status_fatura}" — abra pela tela de Faturas.`, 'warning', 10000);
                return;
            }
            abertaIdRef.current = faturaId;
            setAberta(data as Fatura);
        } finally {
            setAbrindo(null);
        }
    }, [addToast, liberarTrava]);

    const fechar = useCallback(() => {
        const id = abertaIdRef.current;
        abertaIdRef.current = null;
        setAberta(null);
        liberarTrava(id);
        onAtualizarRef.current?.();
    }, [liberarTrava]);

    /** Depois de um passo (emitiu, enviou): relê a fatura e a tela do passo seguinte
     *  abre sozinha. Releitura que chega depois de fechar não reabre nada. */
    const releer = useCallback(async () => {
        const id = abertaIdRef.current;
        if (!id) return;
        const { data } = await supabase.from('faturas').select('*').eq('id', id).maybeSingle();
        if (abertaIdRef.current !== id || !data) return;
        if (STATUS_COM_TELA.has(data.status_fatura)) setAberta(data as Fatura);
        else fechar();
    }, [fechar]);

    const status = aberta?.status_fatura;
    const telas = (
        <>
            <BoletoEmissionModal
                isOpen={status === STATUS_DA_ACAO.emitir}
                onClose={fechar}
                fatura={aberta}
                onUpdate={releer}
                // Igual ao quadro de Faturas: emitiu, não fecha — a releitura troca
                // para a tela de envio.
                onNext={() => { /* fica aberta */ }}
            />
            <BoletoEmitidoModal
                isOpen={status === STATUS_DA_ACAO.enviar}
                onClose={fechar}
                fatura={aberta}
                onUpdate={releer}
            />
            <CobrancaEnviadaModal
                isOpen={status === STATUS_DA_ACAO.abrir}
                onClose={fechar}
                fatura={aberta}
                onUpdate={releer}
            />
        </>
    );

    return { abrir, abrindo, telas };
}
